/**
 * AP-34 18. — **Einstellungen** der Eingangsrechnungen: Freigabemodus, Toleranzen des Abgleichs,
 * Freigaberegeln mit Stufen und Rückfall-Freigeber, Sachkonten, Gemeinkosten-Kategorien mit Zuständigem.
 *
 * Gespeichert wird feldweise ohne Knopf (AP-36). „Regel anlegen" und „Kategorie anlegen" legen
 * an; danach gilt wieder das feldweise Speichern. Die Sachkonten sind eine Zeilenliste: Die leere
 * letzte Zeile legt beim Verlassen an.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Plus, Trash2, Upload } from 'lucide-react'
import { INCOMING_APPROVAL_REASONS } from '@symphenia/shared-types'
import { GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import { Feld as Spalte, ZeilenBlock, ZeilenListe, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige, frage } from '@/lib/dialoge'
import { pageWide } from '@/lib/form-styles'
import { Abschnitt, FeldZeile, SchalterZeile, feldKlasse, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import {
  createApprovalRule,
  createLedgerAccount,
  createOverheadCategory,
  deleteApprovalRule,
  fetchIncomingSettings,
  patchApprovalRule,
  patchIncomingSettings,
  patchLedgerAccount,
  patchOverheadCategory,
} from '../api/incoming-invoices.api'
import { LesungEinstellungen } from '../components/LesungEinstellungen'
import { SachkontenImportDialog } from '../components/SachkontenImportDialog'
import { FREIGEBERART_TEXT, MELDUNG_TEXT, MODUS_TEXT } from '../lib/texte'
import type { ApprovalRule, ApprovalStage, IncomingSettings, LedgerAccountRow, OverheadCategoryRow } from '../types/incoming-invoices.types'

type Setzen = (e: IncomingSettings) => void
const knopf = 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'

export default function IncomingSettingsPage() {
  const [einst, setEinst] = useState<IncomingSettings | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [importOffen, setImportOffen] = useState(false)
  const [importMeldung, setImportMeldung] = useState<string | null>(null)

  const lade = useCallback(async () => {
    try {
      setFehler(null)
      setEinst(await fetchIncomingSettings())
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Einstellungen ließen sich nicht laden.'))
    }
  }, [])
  useEffect(() => {
    void lade()
  }, [lade])

  const tue = async (was: () => Promise<IncomingSettings>, fehlertext: string) => {
    try {
      setFehler(null)
      setEinst(await was())
    } catch (e) {
      setFehler(apiErrorText(e, fehlertext))
      void lade()
    }
  }

  if (!einst) return <div className={pageWide}>{fehler ? <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div> : <p className="text-sm text-muted-foreground">Laden…</p>}</div>

  const regelAnlegen = async () => {
    const name = await frage('Name der neuen Freigaberegel:', '', { pflicht: true, ok: 'Anlegen' })
    if (!name) return
    const rueckfall = einst.rules.find((g) => g.fallbackUserId)?.fallbackUserId ?? einst.approvers[0]?.id
    if (!rueckfall) return setFehler('Es gibt noch niemanden mit dem Recht, Eingangsrechnungen freizugeben. Gib zuerst einem Benutzer die Rolle „Freigabe Eingangsrechnungen".')
    await tue(() => createApprovalRule({ name, fallbackUserId: rueckfall, triggers: [], stages: [{ approverKind: 'COST_OWNER' }] }), 'Die Regel ließ sich nicht anlegen.')
  }
  const kategorieAnlegen = async () => {
    const name = await frage('Name der neuen Gemeinkosten-Kategorie:', '', { pflicht: true, ok: 'Anlegen' })
    if (name) await tue(() => createOverheadCategory(name), 'Die Kategorie ließ sich nicht anlegen.')
  }

  return (
    <div className={pageWide}>
      <div>
        <h1 className="text-3xl font-bold text-foreground">Einstellungen</h1>
        <p className="mt-1 text-sm text-muted-foreground">Eingangsrechnungen: Freigabe, Abgleich, Gemeinkosten. Änderungen speichern sofort und gelten für neue Vorgänge — laufende Freigaben behalten ihre Regel.</p>
      </div>
      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      <Allgemein einst={einst} onEinst={setEinst} />

      <Abschnitt
        titel="Freigaberegeln"
        aktionen={
          <button type="button" className={knopf} onClick={() => void regelAnlegen()}>
            <Plus size={14} /> Regel anlegen
          </button>
        }
      >
        <p className="mb-3 text-sm text-muted-foreground">Ausgewertet wird von oben nach unten; die erste passende Regel gewinnt. Findet eine Stufe niemanden, geht die Aufgabe an den Rückfall-Freigeber. Niemand gibt seine eigene Erfassung frei.</p>
        <div className="space-y-3">
          {einst.rules.map((g) => (
            <Regel key={g.id} regel={g} einst={einst} onEinst={setEinst} tue={tue} />
          ))}
          {einst.rules.length === 0 && <p className="text-sm text-muted-foreground">Keine Regel — im Modus „bei Abweichungen" wird dann jede Rechnung ohne Lauf freigegeben.</p>}
        </div>
      </Abschnitt>

      <Abschnitt
        titel="Sachkonten"
        aktionen={
          <button type="button" className={knopf} onClick={() => setImportOffen(true)}>
            <Upload size={14} /> Importieren
          </button>
        }
      >
        <p className="mb-3 text-sm text-muted-foreground">
          Nummer und Bezeichnung, wie sie im Kontenplan der Buchhaltung stehen — an die Buchhaltung geht die Nummer als Gegenkonto der Buchung. Sobald hier ein Konto steht, erscheint das Sachkonto im Kopf der Rechnung (für alle Positionen) und an
          jeder Position. Abgeschaltete Konten werden nicht mehr angeboten; erfasste Rechnungen behalten sie.
        </p>
        {importMeldung && <p className="mb-3 rounded-md bg-emerald-500/10 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-200">{importMeldung}</p>}
        <ZeilenListe spalten={KONTO_SPALTEN}>
          {einst.accounts.map((k) => (
            <Konto key={k.id} konto={k} onEinst={setEinst} />
          ))}
          <NeuesKonto onEinst={setEinst} />
        </ZeilenListe>
        {importOffen && (
          <SachkontenImportDialog
            onClose={() => setImportOffen(false)}
            onFertig={(neu, meldung) => {
              setEinst(neu)
              setImportMeldung(meldung)
              setImportOffen(false)
            }}
          />
        )}
      </Abschnitt>

      <Abschnitt
        titel="Gemeinkosten-Kategorien"
        aktionen={
          <button type="button" className={knopf} onClick={() => void kategorieAnlegen()}>
            <Plus size={14} /> Kategorie anlegen
          </button>
        }
      >
        <p className="mb-3 text-sm text-muted-foreground">Der Zuständige ist der Kostenverantwortliche der Kategorie: Er bekommt die Freigabe von Rechnungen, die ihr zugeordnet sind. Abgeschaltete Kategorien werden nicht mehr angeboten; erfasste Rechnungen behalten sie.</p>
        {/* Vorschläge für das Sachkonto der Kategorie — es bleibt freier Text, solange kein Stamm geführt wird */}
        <datalist id={SACHKONTEN_LISTE}>
          {einst.accounts
            .filter((k) => k.isActive)
            .map((k) => (
              <option key={k.id} value={k.number} label={`${k.number} ${k.name}`} />
            ))}
        </datalist>
        <div className="overflow-x-auto">
          <div className="min-w-[44rem] divide-y divide-border rounded-md border border-border">
            <div className="grid grid-cols-[minmax(12rem,2fr)_8rem_minmax(12rem,1.5fr)_5rem] gap-2 bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground">
              <span>Name</span>
              <span>Sachkonto</span>
              <span>Zuständiger</span>
              <span>aktiv</span>
            </div>
            {einst.categories.map((k) => (
              <Kategorie key={k.id} kategorie={k} einst={einst} onEinst={setEinst} />
            ))}
          </div>
        </div>
      </Abschnitt>
    </div>
  )
}

function Allgemein({ einst, onEinst }: { einst: IncomingSettings; onEinst: Setzen }) {
  const stelle = useSpeicherstelle<IncomingSettings>({
    schluessel: 'incoming-settings',
    bezeichnung: 'Einstellungen Eingangsrechnungen',
    daten: einst,
    schreibe: async (patch, stand) => {
      const r = await patchIncomingSettings(patch, stand)
      onEinst(r)
      return r
    },
    werte: (a) => a as unknown as Datensatz,
    keepalive: { url: '/incoming-invoices/settings', method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const modus = useFeld<string>(stelle, 'approvalMode', { art: 'auswahl', label: 'Freigabe' })
  const erinnerung = useFeld<number | null>(stelle, 'reminderDays', { art: 'zahl', stellen: 0, pflicht: true, label: 'Erinnerung', pruefe: (w) => (w !== null && (w < 1 || w > 60) ? '1 bis 60 Tage' : null) })
  const lager = useFeld(stelle, 'stockResponsibleUserId', { art: 'auswahl', label: 'Lagerverantwortlicher' })
  const pflicht = useFeld<boolean>(stelle, 'requireCostTarget', { art: 'schalter', label: 'Kostenziel ist Pflicht' })
  const leistung = useFeld<boolean>(stelle, 'requireServiceDate', { art: 'schalter', label: 'Leistungszeitraum ist Pflicht' })
  const kontoPflicht = useFeld<boolean>(stelle, 'requireExpenseAccount', { art: 'schalter', label: 'Sachkonto ist Pflicht' })
  const skonto = useFeld<number | null>(stelle, 'discountWarningDays', { art: 'zahl', stellen: 0, pflicht: true, label: 'Skonto-Warnung' })
  const prozent = useFeld<number | null>(stelle, 'priceTolerancePercent', { art: 'zahl', stellen: 2, pflicht: true, label: 'Preistoleranz %' })
  // Beträge im Preisformat (`geldbetraege.md`): ohne Fokus 1.234,50, beim Bearbeiten ohne Tausenderpunkt
  const betrag = useFeld<number | null>(stelle, 'priceToleranceAmount', { art: 'zahl', schreib: geldEingabe, pflicht: true, label: 'Preistoleranz Betrag' })
  const fracht = useFeld<number | null>(stelle, 'chargeAllowance', { art: 'zahl', schreib: geldEingabe, pflicht: true, label: 'Nebenkosten-Freigrenze' })
  const vierAugen = useFeld<boolean>(stelle, 'paymentRunFourEyes', { art: 'schalter', label: 'Vier-Augen-Prinzip im Zahlungslauf' })
  const agh = useFeld<boolean>(stelle, 'aghEnabled', { art: 'schalter', label: 'Auftraggeberhaftung' })
  const satzWerk = useFeld<number | null>(stelle, 'aghRateWork', { art: 'zahl', stellen: 2, pflicht: true, label: 'Satz Werkvertrag' })
  const satzLeih = useFeld<number | null>(stelle, 'aghRateLeasing', { art: 'zahl', stellen: 2, pflicht: true, label: 'Satz Arbeitskräfteüberlassung' })
  const dlzName = useFeld(stelle, 'aghServiceCenterName', { label: 'Empfänger' })
  const dlzIban = useFeld(stelle, 'aghServiceCenterIban', { label: 'IBAN' })
  const dlzBic = useFeld(stelle, 'aghServiceCenterBic', { label: 'BIC' })
  const dlzZweck = useFeld(stelle, 'aghRemittanceTemplate', { label: 'Verwendungszweck' })
  const deckung = useFeld<number | null>(stelle, 'defaultCoverRetentionPercent', { art: 'zahl', stellen: 2, pflicht: true, label: 'Deckungsrücklass' })
  const haft = useFeld<number | null>(stelle, 'defaultWarrantyRetentionPercent', { art: 'zahl', stellen: 2, pflicht: true, label: 'Haftrücklass' })
  const monate = useFeld<number | null>(stelle, 'defaultWarrantyMonths', { art: 'zahl', stellen: 0, pflicht: true, label: 'Gewährleistung' })

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Abschnitt titel="Freigabe">
        <FeldZeile label="Freigabelauf" status={modus} hilfe={MODUS_TEXT[einst.approvalMode]?.hinweis}>
          <select className={feldKlasse} {...modus.props}>
            {Object.entries(MODUS_TEXT).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </FeldZeile>
        {!einst.matching && einst.approvalMode === 'DEVIATIONS' && (
          <p className="mb-2 flex items-start gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            Ohne das Modul Schriftverkehr gibt es keinen Abgleich mit Bestellungen. Jede Rechnung gilt dann als „ohne Bestellung" — mit der mitgelieferten Regel läuft also jede Rechnung zum Kostenverantwortlichen.
          </p>
        )}
        <FeldZeile label="Erinnerung nach" status={erinnerung} hilfe="Tage — danach bekommt der Freigeber einmal täglich eine Erinnerung">
          <input inputMode="numeric" className={feldKlasse} {...erinnerung.props} />
        </FeldZeile>
        <FeldZeile label="Lagerverantwortlicher" status={lager} hilfe="Kostenverantwortlicher für Positionen mit dem Kostenziel Lager">
          <select className={feldKlasse} {...lager.props}>
            <option value="">— niemand (Rückfall-Freigeber der Regel)</option>
            {einst.approvers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </FeldZeile>
      </Abschnitt>

      <Abschnitt titel="Erfassung und Abgleich">
        <SchalterZeile feld={pflicht} label="Kostenziel ist Pflicht" hinweis="Jede Position braucht Projekt, Lager oder Gemeinkosten, bevor sich die Rechnung abschließen lässt" />
        <SchalterZeile
          feld={kontoPflicht}
          label="Sachkonto ist Pflicht"
          hinweis="Jede Position braucht ihr Sachkonto, bevor sich die Rechnung abschließen lässt — einschalten, wenn du kontiert an die Buchhaltung (BMD) übergibst. Ausgeschaltet steht ein fehlendes Sachkonto nur als Hinweis in der Prüfung"
        />
        <SchalterZeile
          feld={leistung}
          label="Leistungszeitraum ist Pflicht"
          hinweis="Fehlt er, gilt die Rechnung beim Abschließen als formal mangelhaft — ein Freigabeanlass. Ausgeschaltet wird er nicht geprüft"
        />
        <FeldZeile label="Skonto-Warnung" status={skonto} hilfe="Tage vor Ablauf der Skontofrist">
          <input inputMode="numeric" className={feldKlasse} {...skonto.props} />
        </FeldZeile>
        {einst.matching ? (
          <>
            <FeldZeile label="Preistoleranz %" status={prozent} hilfe="So viel darf der Preis über dem vereinbarten liegen — 0 heißt: deckt sich genau">
              <input inputMode="decimal" className={feldKlasse} {...prozent.props} />
            </FeldZeile>
            <FeldZeile label="Preistoleranz Betrag" status={betrag} hilfe="… oder so viel je Position; es genügt, eine der beiden Grenzen einzuhalten">
              <GeldFeldFuer className={feldKlasse} feld={betrag.props} />
            </FeldZeile>
            <FeldZeile label="Nebenkosten-Freigrenze" status={fracht} hilfe="Fracht und Zuschläge ohne Bestellposition bis zu diesem Betrag brauchen keine Freigabe">
              <GeldFeldFuer className={feldKlasse} feld={fracht.props} />
            </FeldZeile>
          </>
        ) : null}
        <SchalterZeile feld={vierAugen} label="Vier-Augen-Prinzip im Zahlungslauf" hinweis="Wer einen Zahlungslauf anlegt, gibt ihn nicht selbst frei" />
      </Abschnitt>

      <LesungEinstellungen />

      <Abschnitt titel="Subunternehmer">
        <SchalterZeile feld={agh} label="Auftraggeberhaftung" hinweis="Für Betriebe, die Bauleistungen an Subunternehmer weitergeben (§ 67a ASVG): HFU-Prüfung am Zahltag, Haftungsbetrag an das Dienstleistungszentrum" />
        {einst.aghEnabled && (
          <>
            <FeldZeile label="Satz Werkvertrag %" status={satzWerk} hilfe="Die Sätze stehen hier, weil sie sich ändern können — bitte bei einer Gesetzesänderung nachführen">
              <input inputMode="decimal" className={feldKlasse} {...satzWerk.props} />
            </FeldZeile>
            <FeldZeile label="Satz Arbeitskräfteüberlassung %" status={satzLeih}>
              <input inputMode="decimal" className={feldKlasse} {...satzLeih.props} />
            </FeldZeile>
            <FeldZeile label="Dienstleistungszentrum" status={dlzName} hilfe="Empfänger des Haftungsbetrags — Name, IBAN und Verwendungszweck nach den Vorgaben der ÖGK">
              <input className={feldKlasse} {...dlzName.props} />
            </FeldZeile>
            <FeldZeile label="IBAN" status={dlzIban}>
              <input className={feldKlasse} {...dlzIban.props} />
            </FeldZeile>
            <FeldZeile label="BIC" status={dlzBic}>
              <input className={feldKlasse} {...dlzBic.props} />
            </FeldZeile>
            <FeldZeile label="Verwendungszweck" status={dlzZweck} hilfe={'Platzhalter: {dienstgebernummer}, {rechnungsnummer}, {eingangsnummer}, {lieferant} — leer gilt „AGH {dienstgebernummer} {rechnungsnummer}"'}>
              <input className={feldKlasse} {...dlzZweck.props} />
            </FeldZeile>
          </>
        )}
      </Abschnitt>

      <Abschnitt titel="Rücklässe">
        <FeldZeile label="Deckungsrücklass %" status={deckung} hilfe="Vorbelegung an Teilrechnungen">
          <input inputMode="decimal" className={feldKlasse} {...deckung.props} />
        </FeldZeile>
        <FeldZeile label="Haftrücklass %" status={haft} hilfe="Vorbelegung an der Schlussrechnung">
          <input inputMode="decimal" className={feldKlasse} {...haft.props} />
        </FeldZeile>
        <FeldZeile label="Gewährleistung" status={monate} hilfe="Monate ab Rechnungsdatum — dann wird der Haftrücklass fällig">
          <input inputMode="numeric" className={feldKlasse} {...monate.props} />
        </FeldZeile>
      </Abschnitt>
    </div>
  )
}

function Regel({ regel, einst, onEinst, tue }: { regel: ApprovalRule; einst: IncomingSettings; onEinst: Setzen; tue: (was: () => Promise<IncomingSettings>, fehlertext: string) => Promise<void> }) {
  const stelle = useSpeicherstelle<IncomingSettings>({
    schluessel: `incoming-rule:${regel.id}`,
    bezeichnung: `Freigaberegel ${regel.name}`,
    daten: { rules: [regel] } as unknown as IncomingSettings,
    schreibe: async (patch, stand) => {
      const r = await patchApprovalRule(regel.id, patch, stand)
      onEinst(r)
      return r
    },
    werte: (a) => ((a.rules ?? []).find((g) => g.id === regel.id) ?? regel) as unknown as Datensatz,
  }) as Speicherstelle<unknown>
  const name = useFeld(stelle, 'name', { label: 'Name', pflicht: true })
  const aktiv = useFeld<boolean>(stelle, 'isActive', { art: 'schalter', label: 'aktiv' })
  const betrag = useFeld<number | null>(stelle, 'minGross', { art: 'zahl', schreib: geldEingabe, label: 'ab Brutto' })
  const rueckfall = useFeld(stelle, 'fallbackUserId', { art: 'auswahl', label: 'Rückfall-Freigeber', pflicht: true })

  // Auslöser und Stufen sind Listen: Sie gehen als Ganzes, mit dem Stand der Regel.
  const schreibe = (patch: Datensatz) => tue(() => patchApprovalRule(regel.id, patch, regel.updatedAt), 'Die Regel ließ sich nicht ändern.')
  const stufen = (neu: ApprovalStage[]) => schreibe({ stages: neu.map((st) => ({ approverKind: st.approverKind, userId: st.userId, roleId: st.roleId, decision: st.decision })) })
  const setzeStufe = (i: number, teil: Partial<ApprovalStage>) => {
    const neu = regel.stages.map((st, n) => (n === i ? { ...st, ...teil } : st))
    const st = neu[i]
    // Eine Stufe „Benutzer" ohne Benutzer oder „Rolle" ohne Rolle wiese der Server ab — mit dem ersten Eintrag vorbelegen.
    if (st.approverKind === 'USER' && !st.userId) st.userId = einst.approvers[0]?.id ?? null
    if (st.approverKind === 'ROLE' && !st.roleId) st.roleId = einst.roles[0]?.id ?? null
    return stufen(neu)
  }
  const loeschen = async () => {
    if (!(await bestaetige(`Freigaberegel „${regel.name}" löschen? Laufende Freigaben behalten sie.`, { ok: 'Löschen', gefaehrlich: true }))) return
    await tue(() => deleteApprovalRule(regel.id), 'Die Regel ließ sich nicht löschen.')
  }

  return (
    <div className={`rounded-md border border-border p-3 ${regel.isActive ? '' : 'opacity-70'}`}>
      <div className="grid gap-x-6 gap-y-1 lg:grid-cols-2">
        <FeldZeile label="Name" status={name}>
          <input className={feldKlasse} {...name.props} />
        </FeldZeile>
        <FeldZeile label="Rückfall-Freigeber" status={rueckfall}>
          <select className={feldKlasse} {...rueckfall.props}>
            {!regel.fallbackUserId && <option value="">— bitte wählen</option>}
            {einst.approvers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </FeldZeile>
        <FeldZeile label="ab Brutto" status={betrag} hilfe={'leer = jeder Betrag; mit dem Auslöser „Betrag über der Grenze" ist das die Grenze'}>
          <GeldFeldFuer className={feldKlasse} feld={betrag.props} />
        </FeldZeile>
        <SchalterZeile feld={aktiv} label="Regel ist aktiv" />
      </div>
      {!regel.fallbackUserId && (
        <p className="mt-1 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
          <AlertTriangle size={14} /> Ohne Rückfall-Freigeber lässt sich eine Rechnung nicht abschließen, sobald die Stufe niemanden findet.
        </p>
      )}

      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auslöser</p>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
        {INCOMING_APPROVAL_REASONS.map((a) => (
          <label key={a} className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={regel.triggers.includes(a)} onChange={(e) => void schreibe({ triggers: e.target.checked ? [...regel.triggers, a] : regel.triggers.filter((x) => x !== a) })} />
            {MELDUNG_TEXT[a] ?? a}
          </label>
        ))}
      </div>

      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Stufen — nacheinander</p>
      <ol className="mt-1 space-y-1">
        {regel.stages.map((st, i) => (
          <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="w-5 text-right tabular-nums text-muted-foreground">{i + 1}.</span>
            <select aria-label={`Stufe ${i + 1}: wer gibt frei`} className={`${feldKlasse} w-56`} value={st.approverKind} onChange={(e) => void setzeStufe(i, { approverKind: e.target.value as ApprovalStage['approverKind'], userId: null, roleId: null })}>
              {Object.entries(FREIGEBERART_TEXT).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            {st.approverKind === 'USER' && (
              <select aria-label={`Stufe ${i + 1}: Benutzer`} className={`${feldKlasse} w-56`} value={st.userId ?? ''} onChange={(e) => void setzeStufe(i, { userId: e.target.value })}>
                {einst.approvers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            )}
            {st.approverKind === 'ROLE' && (
              <>
                <select aria-label={`Stufe ${i + 1}: Rolle`} className={`${feldKlasse} w-56`} value={st.roleId ?? ''} onChange={(e) => void setzeStufe(i, { roleId: e.target.value })}>
                  {einst.roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <select aria-label={`Stufe ${i + 1}: wie viele`} className={`${feldKlasse} w-40`} value={st.decision} onChange={(e) => void setzeStufe(i, { decision: e.target.value as ApprovalStage['decision'] })}>
                  <option value="ANY">einer genügt</option>
                  <option value="ALL">alle müssen</option>
                </select>
              </>
            )}
            {st.approverKind === 'COST_OWNER' && <span className="text-xs text-muted-foreground">Projektleitung, Zuständiger der Gemeinkosten-Kategorie, Lagerverantwortlicher</span>}
            {st.approverKind === 'ORDER_CLERK' && <span className="text-xs text-muted-foreground">wer die Bestellung angelegt hat</span>}
            {regel.stages.length > 1 && (
              <button type="button" className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" title="Stufe entfernen" aria-label={`Stufe ${i + 1} entfernen`} onClick={() => void stufen(regel.stages.filter((_, n) => n !== i))}>
                <Trash2 size={14} />
              </button>
            )}
          </li>
        ))}
      </ol>
      <div className="mt-2 flex flex-wrap gap-2">
        {regel.stages.length < 5 && (
          <button type="button" className={knopf} onClick={() => void stufen([...regel.stages, { approverKind: 'COST_OWNER', userId: null, roleId: null, decision: 'ANY' }])}>
            <Plus size={14} /> Stufe
          </button>
        )}
        <button type="button" className={`${knopf} text-destructive`} onClick={() => void loeschen()}>
          <Trash2 size={14} /> Regel löschen
        </button>
      </div>
    </div>
  )
}

function Kategorie({ kategorie, einst, onEinst }: { kategorie: OverheadCategoryRow; einst: IncomingSettings; onEinst: Setzen }) {
  const stelle = useSpeicherstelle<IncomingSettings>({
    schluessel: `incoming-category:${kategorie.id}`,
    bezeichnung: `Kategorie ${kategorie.name}`,
    daten: { categories: [kategorie] } as unknown as IncomingSettings,
    schreibe: async (patch, stand) => {
      const r = await patchOverheadCategory(kategorie.id, patch, stand)
      onEinst(r)
      return r
    },
    werte: (a) => ((a.categories ?? []).find((k) => k.id === kategorie.id) ?? kategorie) as unknown as Datensatz,
  }) as Speicherstelle<unknown>
  const name = useFeld(stelle, 'name', { label: 'Name', pflicht: true })
  const konto = useFeld(stelle, 'expenseAccount', { label: 'Sachkonto' })
  const zustaendig = useFeld(stelle, 'responsibleUserId', { art: 'auswahl', label: 'Zuständiger' })
  const aktiv = useFeld<boolean>(stelle, 'isActive', { art: 'schalter', label: 'aktiv' })
  const fehler = name.fehler ?? konto.fehler ?? zustaendig.fehler ?? aktiv.fehler
  return (
    <div className={`px-3 py-1.5 ${kategorie.isActive ? '' : 'opacity-60'}`}>
      <div className="grid grid-cols-[minmax(12rem,2fr)_8rem_minmax(12rem,1.5fr)_5rem] items-center gap-2">
        <input aria-label="Name der Kategorie" className={feldKlasse} {...name.props} />
        <input aria-label="Sachkonto" title="Vorschlag für Positionen dieser Kategorie" list={SACHKONTEN_LISTE} className={`${feldKlasse} tabular-nums`} {...konto.props} />
        <select aria-label="Zuständiger" className={feldKlasse} {...zustaendig.props}>
          <option value="">— niemand</option>
          {einst.approvers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <input type="checkbox" aria-label={`${kategorie.name} aktiv`} {...aktiv.schalter} />
      </div>
      {fehler && <p className="mt-1 text-xs text-destructive">{fehler}</p>}
    </div>
  )
}

const SACHKONTEN_LISTE = 'eingangsrechnung-sachkonten'

const KONTO_SPALTEN: SpaltenDef[] = [
  { key: 'number', label: 'Nummer', spur: '7rem', min: 90, schmal: 'w-28' },
  { key: 'name', label: 'Bezeichnung', spur: 'minmax(12rem,1fr)', min: 190, schmal: 'w-80' },
  { key: 'isActive', label: 'aktiv', spur: '3rem', min: 44, schmal: 'w-12' },
]

/** Ein Sachkonto des Stamms: Nummer, Bezeichnung, aktiv — die Zeile speichert beim Verlassen. */
function Konto({ konto, onEinst }: { konto: LedgerAccountRow; onEinst: Setzen }) {
  const stelle = useSpeicherstelle<IncomingSettings>({
    schluessel: `incoming-account:${konto.id}`,
    bezeichnung: `Sachkonto ${konto.number}`,
    daten: { accounts: [konto] } as unknown as IncomingSettings,
    schreibe: async (patch, stand) => {
      const r = await patchLedgerAccount(konto.id, patch, stand)
      onEinst(r)
      return r
    },
    werte: (a) => ((a.accounts ?? []).find((k) => k.id === konto.id) ?? konto) as unknown as Datensatz,
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: `Sachkonto ${konto.number}` })
  const nummer = useFeld(stelle, 'number', { gruppe, label: 'Nummer', pflicht: true })
  const name = useFeld(stelle, 'name', { gruppe, label: 'Bezeichnung', pflicht: true })
  const aktiv = useFeld<boolean>(stelle, 'isActive', { gruppe, art: 'schalter', label: 'aktiv' })
  return (
    <ZeilenBlock gruppe={gruppe} schluessel={konto.id} gedimmt={!konto.isActive}>
      <Spalte spalte="number">
        <input aria-label="Nummer" data-start className={`${zelle} tabular-nums`} {...nummer.props} />
      </Spalte>
      <Spalte spalte="name">
        <input aria-label="Bezeichnung" className={zelle} {...name.props} />
      </Spalte>
      <Spalte spalte="isActive">
        <input type="checkbox" aria-label={`Sachkonto ${konto.number} aktiv`} {...aktiv.schalter} />
      </Spalte>
    </ZeilenBlock>
  )
}

/**
 * Die leere letzte Zeile legt beim Verlassen an (`speichern.md`, Zeilenliste). Nach dem Anlegen über die
 * Tastatur steht die nächste leere Zeile bereit — ein Kontenplan hat schnell fünfzig Zeilen.
 */
function NeuesKonto({ onEinst }: { onEinst: Setzen }) {
  const [neu, setNeu] = useState({ number: '', name: '' })
  const [fehler, setFehler] = useState<string | null>(null)
  const [runde, setRunde] = useState(0)
  const anlegen = async (perTastatur: boolean) => {
    const number = neu.number.trim()
    const name = neu.name.trim()
    if (!number && !name) return setFehler(null)
    if (!number || !name) return setFehler('Ein Sachkonto braucht Nummer und Bezeichnung.')
    try {
      setFehler(null)
      onEinst(await createLedgerAccount(number, name))
      setNeu({ number: '', name: '' })
      if (perTastatur) setRunde((n) => n + 1)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Sachkonto ließ sich nicht anlegen.'))
    }
  }
  return (
    <ZeilenBlock
      key={runde}
      neu
      autoFokus={runde > 0}
      fehler={fehler}
      onVerlassen={(perTastatur) => void anlegen(perTastatur)}
      onVerwerfen={() => {
        setNeu({ number: '', name: '' })
        setFehler(null)
      }}
    >
      <Spalte spalte="number">
        <input aria-label="Nummer des neuen Sachkontos" data-start placeholder="Nummer" className={`${zelle} tabular-nums`} value={neu.number} onChange={(e) => setNeu((alt) => ({ ...alt, number: e.target.value }))} />
      </Spalte>
      <Spalte spalte="name">
        <input aria-label="Bezeichnung des neuen Sachkontos" placeholder="Neues Sachkonto — Bezeichnung" className={zelle} value={neu.name} onChange={(e) => setNeu((alt) => ({ ...alt, name: e.target.value }))} />
      </Spalte>
      <Spalte spalte="isActive" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}
